import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import type { PmAccessLogEntry, PmVisitStatus } from '@/types';

const STATUS_LABELS: Record<PmVisitStatus, string> = {
  granted: 'On Property',
  checked_out: 'Checked Out',
  expired: 'Expired',
  denied: 'Denied',
  pending: 'Pending',
};

const STATUS_VARIANTS: Record<PmVisitStatus, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    granted: 'default',
    checked_out: 'secondary',
    expired: 'outline',
    denied: 'destructive',
    pending: 'secondary',
  };

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

interface EventDetailPanelProps {
  entry: PmAccessLogEntry | null;
  onClose: () => void;
  onFlag?: (entry: PmAccessLogEntry) => void;
  onClearFlag?: (entry: PmAccessLogEntry) => void;
  isFlagPending?: boolean;
}

export function EventDetailPanel({
  entry,
  onClose,
  onFlag,
  onClearFlag,
  isFlagPending,
}: EventDetailPanelProps) {
  if (!entry) return null;

  const checkinTime = new Date(entry.timestamp).toLocaleString();
  const checkoutTime = entry.checkedOutAt ? new Date(entry.checkedOutAt).toLocaleString() : null;
  const duration = entry.durationMinutes
    ? entry.durationMinutes >= 60
      ? `${Math.floor(entry.durationMinutes / 60)}h ${entry.durationMinutes % 60}m`
      : `${entry.durationMinutes}m`
    : entry.status === 'granted'
      ? 'On property'
      : null;

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="w-[360px] sm:w-[360px] overflow-y-auto" showCloseButton={false}>
        <SheetHeader className="flex flex-row items-center justify-between mb-4">
          <SheetTitle className="text-base">Visit Detail</SheetTitle>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </SheetHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={STATUS_VARIANTS[entry.status]}>{STATUS_LABELS[entry.status]}</Badge>
            <span className="text-xs text-muted-foreground">{checkinTime}</span>
          </div>

          {entry.status === 'granted' && (onFlag ?? onClearFlag) && (
            <div
              className={[
                'rounded-md border p-3 space-y-2',
                entry.isFlagged ? 'border-destructive bg-destructive/5' : 'border-border',
              ].join(' ')}
            >
              {entry.isFlagged ? (
                <>
                  <p className="text-sm font-semibold text-destructive">Flagged — do not release</p>
                  {entry.flagReason && (
                    <p className="text-xs text-muted-foreground">{entry.flagReason}</p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={isFlagPending}
                    onClick={() => onClearFlag?.(entry)}
                  >
                    Clear flag
                  </Button>
                </>
              ) : (
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full"
                  disabled={isFlagPending}
                  onClick={() => onFlag?.(entry)}
                >
                  Flag visitor (block exit)
                </Button>
              )}
            </div>
          )}

          <div className="rounded-md bg-muted/50 p-3 space-y-1">
            <Row label="Visitor" value={entry.visitorName} />
            <Row label="Host Unit" value={entry.hostUnit} />
            <Row label="Host" value={entry.hostName} />
          </div>

          <div className="rounded-md bg-muted/50 p-3 space-y-1">
            <Row label="Gate" value={entry.gateName} />
            <Row
              label="Method"
              value={entry.method === 'qr' ? 'QR Code' : entry.method === 'pin' ? 'PIN' : null}
            />
            <Row label="Guard" value={entry.guardName} />
            <Row label="Check-in" value={checkinTime} />
            {checkoutTime && <Row label="Checked out" value={checkoutTime} />}
            {duration && <Row label="Duration" value={duration} />}
          </div>

          <div className="rounded-md bg-muted/50 p-3 space-y-1">
            <Row label="Pass type" value={entry.invitationType?.replace(/_/g, ' ') ?? 'Walk-in'} />
            <Row label="Invitation ID" value={entry.invitationId} />
          </div>

          {(entry.vehiclePlate !== null || entry.vehicleMake !== null) && (
            <div className="rounded-md bg-muted/50 p-3 space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Vehicle</p>
              <Row label="Plate" value={entry.vehiclePlate} />
              <Row label="Make" value={entry.vehicleMake} />
              <Row label="Colour" value={entry.vehicleColour} />
            </div>
          )}

          {entry.entryInstructions && (
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                Entry Instructions
              </p>
              <p className="text-sm">{entry.entryInstructions}</p>
            </div>
          )}

          {entry.timeline.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Timeline</p>
              <ol className="relative border-l border-border space-y-3 pl-4">
                {entry.timeline.map((t, i) => (
                  <li key={i} className="relative">
                    <div className="absolute -left-[17px] top-1 h-2 w-2 rounded-full bg-primary" />
                    <p className="text-sm font-medium">{t.event}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(t.at).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
